/**
 * Image Processing Utilities
 *
 * Handles image resizing and aspect ratio fitting for the project.
 * All images are fitted to the project's aspect ratio with width fitting.
 */

import { AspectRatio, ASPECT_RATIOS } from '@/types';

/**
 * Fit an image to the specified aspect ratio using object-fit: cover behavior
 * Strategy: Fill the aspect ratio box completely, crop overflow
 *
 * Web version uses 1280px max width for optimal performance.
 *
 * @param imageUrl - Source image URL (blob URL or data URL)
 * @param aspectRatio - Target aspect ratio
 * @param maxWidth - Maximum width for the output (default: 1280 for web)
 * @param offsetX - Horizontal offset for cropping (-1 to 1, 0 = centered)
 * @param offsetY - Vertical offset for cropping (-1 to 1, 0 = centered)
 * @param scale - Scale/zoom factor (1 = 100%, default: 1)
 * @returns Promise<Blob> - Processed image as a blob
 */
export async function fitImageToAspectRatio(
  imageUrl: string,
  aspectRatio: AspectRatio,
  maxWidth: number = 1280,
  offsetX: number = 0,
  offsetY: number = 0,
  scale: number = 1
): Promise<Blob> {
  const targetDimensions = ASPECT_RATIOS[aspectRatio];

  // Calculate target dimensions maintaining the aspect ratio
  const targetWidth = Math.min(maxWidth, targetDimensions.width);
  const targetHeight = Math.round(targetWidth / targetDimensions.ratio);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        // Create canvas with target dimensions
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Fill with black background
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, targetWidth, targetHeight);

        // Calculate image and target aspect ratios
        const imageAspectRatio = img.width / img.height;
        const targetAspectRatio = targetWidth / targetHeight;

        // Calculate scaling to cover the target area (like object-fit: cover)
        let drawWidth: number;
        let drawHeight: number;

        if (imageAspectRatio > targetAspectRatio) {
          // Image is wider than target - fit to height, crop sides
          drawHeight = targetHeight;
          drawWidth = drawHeight * imageAspectRatio;
        } else {
          // Image is taller than target - fit to width, crop top/bottom
          drawWidth = targetWidth;
          drawHeight = drawWidth / imageAspectRatio;
        }

        // Apply scale (zoom)
        drawWidth *= scale;
        drawHeight *= scale;

        // Calculate position based on offset
        // Center by default, then apply offset
        let drawX = (targetWidth - drawWidth) / 2;
        let drawY = (targetHeight - drawHeight) / 2;

        // Apply offset: -1 = left/top, 0 = center, 1 = right/bottom
        // When zoomed (scale > 1), there's always overflow in both directions
        // When not zoomed, only allow offset in the direction that overflows
        // Note: Canvas drawing is inverted - moving image right shows left side
        // So we need to negate the offset for canvas drawing
        if (scale > 1 || imageAspectRatio > targetAspectRatio) {
          // Can offset horizontally
          const maxOffsetX = Math.abs(drawX);
          drawX -= offsetX * maxOffsetX; // Negate for canvas (opposite of CSS)
        }

        if (scale > 1 || imageAspectRatio <= targetAspectRatio) {
          // Can offset vertically
          const maxOffsetY = Math.abs(drawY);
          drawY -= offsetY * maxOffsetY; // Negate for canvas (opposite of CSS)
        }

        // Draw the image
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          0.85
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = imageUrl;
  });
}

/**
 * Get dimensions for displaying an image in a container while maintaining aspect ratio
 *
 * @param containerWidth - Width of the container
 * @param containerHeight - Height of the container
 * @param aspectRatio - Target aspect ratio
 * @returns Object with width and height to use for display
 */
export function getDisplayDimensions(
  containerWidth: number,
  containerHeight: number,
  aspectRatio: AspectRatio
): { width: number; height: number } {
  const targetRatio = ASPECT_RATIOS[aspectRatio].ratio;

  // Try fitting by width
  let width = containerWidth;
  let height = width / targetRatio;

  // If height exceeds container, fit by height instead
  if (height > containerHeight) {
    height = containerHeight;
    width = height * targetRatio;
  }

  return { width, height };
}

/**
 * Convert a Blob to a data URL
 *
 * @param blob - Blob to convert
 * @returns Promise<string> - Data URL
 */
export function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Resize an image to fit within maximum dimensions while maintaining aspect ratio
 * Optimizes file size for web storage and performance
 *
 * Web version uses 1280x720 for optimal performance and storage efficiency.
 * Desktop Pro version will support higher resolutions (1920x1080, 4K).
 *
 * All images are converted to JPEG format for maximum compression.
 * Transparent PNGs are composited onto a dark grey (#2a2a2a) background.
 *
 * @param file - Image file to resize
 * @param maxWidth - Maximum width (default: 1280 for web)
 * @param maxHeight - Maximum height (default: 720 for web)
 * @param quality - JPEG quality 0-1 (default: 0.85)
 * @returns Promise<Blob> - Resized image as JPEG blob
 */
export async function resizeImageForImport(
  file: File,
  maxWidth: number = 1280,
  maxHeight: number = 720,
  quality: number = 0.85
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      try {
        // Release the object URL
        URL.revokeObjectURL(url);

        // Check if resize is needed
        if (img.width <= maxWidth && img.height <= maxHeight) {
          // Image is already small enough - return original
          resolve(file);
          return;
        }

        // Calculate new dimensions maintaining aspect ratio
        let newWidth = img.width;
        let newHeight = img.height;

        if (img.width > maxWidth) {
          newWidth = maxWidth;
          newHeight = (img.height * maxWidth) / img.width;
        }

        if (newHeight > maxHeight) {
          newHeight = maxHeight;
          newWidth = (img.width * maxHeight) / img.height;
        }

        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(newWidth);
        canvas.height = Math.round(newHeight);
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Use better quality settings for resizing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Fill with dark grey background (for transparent PNGs)
        // This allows us to convert all images to JPEG for better compression
        ctx.fillStyle = '#2a2a2a'; // Dark grey background
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw resized image (transparency will blend with dark grey)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Convert to blob
        // Always use JPEG for maximum compression (transparency replaced with dark grey)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
